import React, { useState } from 'react';
import { PropertyEvent, PropertyOwnerStay } from '../services/propertyAPI';
import { fromDefaultEvents } from '../utils/defaultEvents';

interface Props {
  properties: { id?: string; name: string; purchase_date?: string }[];
  startDate: string;          // analysis window, for loading the default calendar (F19)
  endDate: string;
  events: PropertyEvent[];
  onEventsChange: (e: PropertyEvent[]) => void;
  ownerStays: PropertyOwnerStay[];
  onOwnerStaysChange: (s: PropertyOwnerStay[]) => void;
}

const newId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

// A small editor for user-defined events (premium ADR/occupancy windows) and owner
// stays (blocked weeks). It populates the `events` / `ownerStays` arrays the portfolio
// page already sends to /analyze; leaving them empty keeps the default Cannes calendar.
const EventsOwnerStaysEditor: React.FC<Props> = ({
  properties, startDate, endDate, events, onEventsChange, ownerStays, onOwnerStaysChange,
}) => {
  const [loadError, setLoadError] = useState<string | null>(null);
  const firstPid = properties[0]?.id || '';
  const [ev, setEv] = useState({
    property_id: firstPid, name: '', start_date: '', end_date: '', occupancy_pct: 90, adr: 500, probability: 1,
  });
  const [st, setSt] = useState({ property_id: firstPid, start_date: '', end_date: '', purpose: 'Personal' });

  const addEvent = () => {
    if (!ev.property_id || !ev.start_date || !ev.end_date) return;
    onEventsChange([...events, { ...ev, id: newId(), created_at: new Date().toISOString() } as PropertyEvent]);
    setEv({ ...ev, name: '', start_date: '', end_date: '' });
  };
  const addStay = () => {
    if (!st.property_id || !st.start_date || !st.end_date) return;
    const days = (new Date(st.end_date).getTime() - new Date(st.start_date).getTime()) / 86400000;
    const duration = Math.max(1, Math.round(days));
    onOwnerStaysChange([
      ...ownerStays,
      { ...st, id: newId(), duration, created_at: new Date().toISOString() } as PropertyOwnerStay,
    ]);
    setSt({ ...st, start_date: '', end_date: '' });
  };
  // F19: load the default Cannes calendar for the chosen property and window, as
  // ordinary editable events (adding any event replaces the default calendar).
  const loadDefaultCalendar = async () => {
    const pid = ev.property_id;
    if (!pid) return;
    setLoadError(null);
    try {
      const base = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
      const purchase = properties.find(p => p.id === pid)?.purchase_date;
      const qs = new URLSearchParams({ start_date: startDate, end_date: endDate, ...(purchase ? { purchase_date: purchase } : {}) });
      const resp = await fetch(`${base}/api/v1/portfolio/default-events?${qs}`);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const loaded = fromDefaultEvents((await resp.json()).events, pid);
      const existing = new Set(events.filter(e => e.property_id === pid).map(e => `${e.start_date}|${e.name}`));
      onEventsChange([...events, ...loaded.filter(e => !existing.has(`${e.start_date}|${e.name}`))]);
    } catch (e: any) {
      setLoadError(`Could not load the Cannes calendar: ${e?.message || e}`);
    }
  };
  const nameFor = (pid: string) => properties.find(p => p.id === pid)?.name || pid;

  if (properties.length === 0) return null;

  const inputCls = 'border border-gray-300 rounded px-2 py-1 text-sm';
  const addBtn = 'px-3 py-1 bg-vista-teal-600 text-white rounded text-sm hover:bg-vista-teal-700';

  return (
    <div className="bg-white rounded-lg shadow-md p-6 mb-6">
      <h3 className="text-lg font-semibold text-gray-800 mb-1">Events &amp; owner stays (optional)</h3>
      <p className="text-sm text-gray-600 mb-4">
        Add your own high-demand dates, or block weeks you&apos;ll use yourself. Leave both
        empty to fall back to the default Cannes calendar. Adding any event for a property
        replaces that calendar for it, so load the calendar first to keep and edit it.
        The projection assumes every event happens; Monte Carlo draws each event&apos;s
        probability (cancellation risk).
      </p>

      {/* Events */}
      <div className="mb-6">
        <div className="text-sm font-medium text-gray-700 mb-2">Events (premium ADR / occupancy for a date range)</div>
        <div className="grid grid-cols-2 md:grid-cols-7 gap-2 mb-2">
          <select className={inputCls} value={ev.property_id} onChange={e => setEv({ ...ev, property_id: e.target.value })}>
            {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <input className={inputCls} placeholder="Name" value={ev.name} onChange={e => setEv({ ...ev, name: e.target.value })} />
          <input type="date" className={inputCls} value={ev.start_date} onChange={e => setEv({ ...ev, start_date: e.target.value })} />
          <input type="date" className={inputCls} value={ev.end_date} onChange={e => setEv({ ...ev, end_date: e.target.value })} />
          <input type="number" className={inputCls} placeholder="Occ %" value={ev.occupancy_pct} onChange={e => setEv({ ...ev, occupancy_pct: parseFloat(e.target.value) || 0 })} />
          <input type="number" className={inputCls} placeholder="ADR €" value={ev.adr} onChange={e => setEv({ ...ev, adr: parseFloat(e.target.value) || 0 })} />
          <input type="number" step="0.01" min="0" max="1" className={inputCls} placeholder="Probability 0-1" title="Chance the event happens in a year (Monte Carlo)" value={ev.probability} onChange={e => setEv({ ...ev, probability: e.target.value === '' ? 1 : parseFloat(e.target.value) })} />
        </div>
        <div className="flex gap-2">
          <button onClick={addEvent} className={addBtn}>Add event</button>
          <button onClick={loadDefaultCalendar} className="px-3 py-1 border border-vista-teal-600 text-vista-teal-700 rounded text-sm hover:bg-vista-teal-50">Load Cannes calendar</button>
        </div>
        {loadError && <div className="text-sm text-red-600 mt-2">{loadError}</div>}
        {events.length > 0 && (
          <ul className="mt-3 space-y-1">
            {events.map(e => (
              <li key={e.id} className="flex items-center justify-between text-sm bg-gray-50 rounded px-3 py-1">
                <span>{nameFor(e.property_id)}: {e.name || 'Event'} {e.start_date}&rarr;{e.end_date}, {e.occupancy_pct}% @ &euro;{e.adr}{e.probability != null && e.probability < 1 ? `, ${Math.round(e.probability * 100)}% likely` : ''}</span>
                <button onClick={() => onEventsChange(events.filter(x => x.id !== e.id))} className="text-red-600 hover:underline">Remove</button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Owner stays */}
      <div>
        <div className="text-sm font-medium text-gray-700 mb-2">Owner stays (block your own weeks - no rental income)</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-2">
          <select className={inputCls} value={st.property_id} onChange={e => setSt({ ...st, property_id: e.target.value })}>
            {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <input type="date" className={inputCls} value={st.start_date} onChange={e => setSt({ ...st, start_date: e.target.value })} />
          <input type="date" className={inputCls} value={st.end_date} onChange={e => setSt({ ...st, end_date: e.target.value })} />
          <input className={inputCls} placeholder="Purpose" value={st.purpose} onChange={e => setSt({ ...st, purpose: e.target.value })} />
        </div>
        <button onClick={addStay} className={addBtn}>Add owner stay</button>
        {ownerStays.length > 0 && (
          <ul className="mt-3 space-y-1">
            {ownerStays.map(s => (
              <li key={s.id} className="flex items-center justify-between text-sm bg-gray-50 rounded px-3 py-1">
                <span>{nameFor(s.property_id)}: {s.start_date}&rarr;{s.end_date} ({s.purpose})</span>
                <button onClick={() => onOwnerStaysChange(ownerStays.filter(x => x.id !== s.id))} className="text-red-600 hover:underline">Remove</button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default EventsOwnerStaysEditor;
